'use client';

/**
 * CanvasCardShell — testability wrapper for every canvas card mount.
 *
 * Emits the dual identifier convention named in
 * `klyde_testing/docs/canvas_testability.md`:
 *
 *   data-canvas-card={content_key}   // human-readable, engineer-thinkable
 *   data-card-sid={CD_*}             // graph-native, tool-thinkable
 *
 * Used by LayoutEngine for matrix-placement cards, and by any
 * composition that mounts a card outside the matrix path (e.g. the
 * renderer slot's stacked composition for the test_run canvas). One
 * source of truth for the testability contract — adding a new
 * attribute happens here, not 90× across cards.
 *
 * The semantic_id is looked up from useTotalMatrixStore.cardIndex,
 * which is populated by `/ui/total-matrix.card_index` (deterministic
 * from `compute_semantic_id("CD", content_key)` server-side). When
 * the index isn't loaded (SSR / pre-fetch), only data-canvas-card
 * renders — graceful degradation, tests that only need the readable
 * selector still work.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';


/** Mirrors React Query's status union. Cards with no query lifecycle
 *  (pure presentational shells) leave `state` undefined and the
 *  attribute doesn't render — tests then wait only on cards that
 *  actually have data. */
export type CanvasCardState = 'idle' | 'pending' | 'success' | 'error';


interface CanvasCardShellProps {
  contentKey: string;
  className?: string;
  style?: React.CSSProperties;
  /** Card's data-loading state, exposed as `data-state` on the shell
   *  so Playwright can wait without coupling to internal markup:
   *      page.locator('[data-canvas-card="X"][data-state="success"]').waitFor()
   *  Cards pass their React Query status (e.g. `query.status`). */
  state?: CanvasCardState;
  children: React.ReactNode;
}


export function CanvasCardShell({
  contentKey, className, style, state, children,
}: CanvasCardShellProps) {
  const cardSid = useTotalMatrixStore((s) => s.cardIndex?.[contentKey] ?? null);
  return (
    <div
      className={className}
      style={style}
      data-canvas-card={contentKey}
      data-card-sid={cardSid ?? undefined}
      data-state={state}
    >
      {children}
    </div>
  );
}
